/*
 * BulkImportManager reducer
 */

/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import {
  // Main Actions
  uploadFileAct,
  uploadFileProgressAct,
  analyzeFileAct,
  getRequiredFieldsAct,
  createImportJobAct,

  // WebSocket Actions
  websocketStatusAct,

  // Job Progress Actions
  jobProgressAct,

  // Column Mapping Actions
  updateColumnMappingAct,
  clearColumnMappingAct,

  // Navigation Actions
  setCurrentStepAct,
  goToNextStepAct,

  // Reset Actions
  resetAllAct,
  selectImportTypeAct,
} from './actions';

const initialState = {
  // File Upload State
  uploadLoadingState: 0, // 0: idle, 1: uploading, 2: success, 3: error
  uploadProgress: 0,
  uploadedFile: null,
  uploadId: null,
  fileId: null,

  importType: '',

  // File Analysis State
  analysisLoadingState: 0, // 0: idle, 1: analyzing, 2: success, 3: error
  analysisResult: null,

  // Required Fields State
  requiredFieldsLoadingState: 0, // 0: idle, 1: loading, 2: success, 3: error
  requiredFields: [],

  // Column Mapping State
  columnMapping: {},

  // Import Job State
  importJobLoadingState: 0, // 0: idle, 1: creating, 2: success, 3: error
  currentJob: null,

  // Job Progress State (from WebSocket)
  jobProgress: {
    status: 'idle', // idle, pending, in_progress, completed, failed, cancelled
    totalRows: 0,
    processedRows: 0,
    successfulRows: 0,
    failedRows: 0,
    errors: [],
  },

  // WebSocket State
  isConnected: false,

  // UI State
  currentStep: 'select', // upload, analyze, mapping, import, results
};

const bulkImportSlice = createSlice({
  name: 'bulkImport',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // File Upload Cases
      .addCase(uploadFileAct, (state, action) => {
        const { loadingState, file, progress, uploadId, fileId } =
          action.payload;
        state.uploadLoadingState = loadingState;

        switch (loadingState) {
          case 1: // uploading
            state.uploadProgress = progress || 0;
            state.uploadId = uploadId;
            break;
          case 2: // success
            state.uploadedFile = file;
            state.uploadProgress = progress || 100;
            state.fileId = fileId;
            state.uploadId = uploadId;
            state.currentStep = 'analyze';
            break;
          case 3: // error
            state.uploadProgress = 0;
            break;
          default:
            break;
        }
      })
      .addCase(uploadFileProgressAct, (state, action) => {
        state.uploadProgress = action.payload.progress;
      })

      // File Analysis Cases
      .addCase(analyzeFileAct, (state, action) => {
        const { loadingState, analysisResult } = action.payload;
        state.analysisLoadingState = loadingState;

        switch (loadingState) {
          case 2: // success
            state.analysisResult = analysisResult;
            state.currentStep = 'mapping';
            break;
          default:
            break;
        }
      })

      // Required Fields Cases
      .addCase(getRequiredFieldsAct, (state, action) => {
        const { loadingState, requiredFields } = action.payload;
        state.requiredFieldsLoadingState = loadingState;

        switch (loadingState) {
          case 2: // success
            state.requiredFields = requiredFields;
            break;
          default:
            break;
        }
      })

      // Import Job Cases
      .addCase(createImportJobAct, (state, action) => {
        const { loadingState, job } = action.payload;
        state.importJobLoadingState = loadingState;

        switch (loadingState) {
          case 2: // success
            state.currentJob = job;
            state.currentStep = 'import';
            break;
          default:
            break;
        }
      })

      // Job Progress Cases (from WebSocket and polling)
      .addCase(jobProgressAct, (state, action) => {
        const { jobId, status, progress, ...otherData } = action.payload;

        // Update current job info
        if (state.currentJob) {
          state.currentJob = { ...state.currentJob, ...otherData, id: jobId };
        }

        // Update job progress status
        if (status) {
          state.jobProgress.status = status;
        }

        // Update progress data
        if (progress) {
          state.jobProgress = {
            ...state.jobProgress,
            ...progress,
            status: status || state.jobProgress.status,
          };
        }

        // Move to results step when complete
        if (status === 'completed' || status === 'failed') {
          state.currentStep = 'results';
        }
      })

      // WebSocket Cases
      .addCase(websocketStatusAct, (state, action) => {
        const { isConnected } = action.payload;
        state.isConnected = isConnected;
      })

      // Column Mapping Cases
      .addCase(updateColumnMappingAct, (state, action) => {
        state.columnMapping = { ...state.columnMapping, ...action.payload };
      })
      .addCase(clearColumnMappingAct, state => {
        state.columnMapping = {};
      })

      // Navigation Cases
      .addCase(setCurrentStepAct, (state, action) => {
        state.currentStep = action.payload;
      })
      .addCase(goToNextStepAct, state => {
        const steps = ['upload', 'analyze', 'mapping', 'import', 'results'];
        const currentIndex = steps.indexOf(state.currentStep);
        if (currentIndex < steps.length - 1) {
          state.currentStep = steps[currentIndex + 1];
        }
      })
      .addCase(selectImportTypeAct, (state, { payload }) => {
        state.importType = payload;
        state.currentStep = payload ? 'upload' : 'select';
      })
      // Reset Cases
      .addCase(resetAllAct, () => initialState);
  },
});

export default bulkImportSlice.reducer;
